import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import { USER_TYPES } from '../constants/designer';
import { PLAN_IDS, planOf } from '../constants/plans';
import useCredits from '../hooks/useCredits';
import './PlanStrip.css';

// ── YOUR PLAN, AT THE TOP OF THE DASHBOARD ───────────────────────────────────
// One slim row above the banner: which tier this designer is on, and the way up
// from it.
//
// DESIGNERS ONLY. The ladder is not sold to customers or shops — a customer buys
// credits, and showing them a tier they cannot use would be an upsell for the
// wrong product. Same `userType` field the rest of the app branches on.
//
// THE BUTTONS NAVIGATE, THEY DO NOT BUY. Payments are not connected, so every
// one of them goes to /plans where the prices and what each tier includes are
// set out. A button here that appeared to upgrade in place would be exactly the
// fake billing this codebase has avoided everywhere else.
//
// Only tiers ABOVE the current one are offered. On gold there is nothing to
// upgrade to, so the row turns into a link to manage the plan instead — an
// "Upgrade" button that cannot do anything is worse than no button.
export default function PlanStrip() {
  const { t } = useT();
  const { profile } = useAuth();
  const { plan } = useCredits();

  if (profile?.userType !== USER_TYPES.DESIGNER) return null;

  // `useCredits` is the live listener, so this follows a tier change the moment
  // it lands — including one made from the test panel on /plans.
  const current = planOf(plan);
  const higher = PLAN_IDS.slice(PLAN_IDS.indexOf(current.id) + 1);

  return (
    <div className={`pst pst--${current.id}`}>
      <span className="pst-left">
        {/* A crown at the top of the ladder, an upward chevron on every rung
            below it — the glyph says which of the two states this is before
            the words are read. */}
        <span className="pst-ico" aria-hidden="true">
          {current.id === 'gold' ? (
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <path d="M3 7l4.5 3.5L12 4l4.5 6.5L21 7l-1.7 11H4.7L3 7z" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
              strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 15l6-6 6 6" />
            </svg>
          )}
        </span>
        <span className="pst-text">
          <span className="pst-line">
            <span className="pst-label">{t('pst_your_plan')}</span>
            <span className="pst-name">{t(`pl_name_${current.id}`)}</span>
          </span>
          {/* The reason to move, in the words of what it buys — placement —
              rather than "upgrade", which only names the transaction. On gold
              it becomes a statement instead of a pitch. */}
          <span className="pst-msg">{t(`pst_msg_${current.id}`)}</span>
        </span>
      </span>

      <span className="pst-actions">
        {higher.length === 0 ? (
          <Link to="/plans" className="pst-btn pst-btn--ghost">{t('pst_manage')}</Link>
        ) : (
          higher.map(id => (
            <Link
              key={id}
              to="/plans"
              // The top tier available is the emphasised one; the steps between
              // are quieter so the row does not read as three equal shouts.
              // Gold gets its own treatment rather than the brand blue: the
              // tier is named after a metal and the button people are being
              // pointed at should look like it. The rungs below stay quiet so
              // the row still has one obvious destination.
              className={`pst-btn ${
                id === 'gold' ? 'pst-btn--gold'
                  : id === higher[higher.length - 1] ? 'pst-btn--go' : 'pst-btn--ghost'
              }`}
            >
              {t('pst_upgrade').replace('{p}', t(`pl_name_${id}`))}
            </Link>
          ))
        )}
      </span>
    </div>
  );
}
